import React, { useEffect, useState } from "react";
import { Container, Row, Col, Spinner, Card, Badge } from "react-bootstrap";
import axios from "axios";
import Layout from "../../../layout/default";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { FaBoxOpen, FaCalendarAlt, FaRupeeSign, FaKey, FaShoppingBag } from "react-icons/fa";

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasOrders, setHasOrders] = useState(false);
  const userId = localStorage.getItem("user_id");
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        navigate('/shop/auths/login', { replace: true });
        Swal.fire({
          icon: "warning",
          title: "Authentication Required",
          text: "Please log in to view your orders.",
        });
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_HOST}api/orders/userOrders`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status === 200) {
        if (response.data.success) {
          setOrders(response.data.data || []);
          setHasOrders(response.data.data && response.data.data.length > 0);
          if (!response.data.data || response.data.data.length === 0) {
            await Swal.fire({
              icon: "info",
              title: "No Orders Found",
              text: "You have no orders at the moment.",
              timer: 5000,
              timerProgressBar: true,
              showConfirmButton: false,
            });
          }
        } else {
          if (response.data.message === "No orders found for this user") {
            setOrders([]);
            setHasOrders(false);
            await Swal.fire({
              icon: "info",
              title: "No Orders Found",
              text: "You have no orders at the moment.",
              timer: 5000,
              timerProgressBar: true,
              showConfirmButton: false,
            });
          } else {
            await Swal.fire({
              icon: "error",
              title: "Failed to Fetch Orders",
              text: "An error occurred while fetching your orders.",
              timer: 5000,
              timerProgressBar: true,
              showConfirmButton: false,
            });
          }
        }
      } else {
        await Swal.fire({
          icon: "error",
          title: "Failed to Fetch Orders",
          text: "An error occurred while fetching your orders.",
          timer: 5000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error(error);
      if (error.response && error.response.data && error.response.data.success === false) {
        setOrders([]);
        setHasOrders(false);
        // await Swal.fire({
        //   icon: 'info',
        //   title: 'No Orders Found',
        //   text: 'You have no orders at the moment.',
        //   timer: 5000,
        //   timerProgressBar: true,
        //   showConfirmButton: false,
        // });
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'An unexpected error occurred. Please try again later.',
          timer: 5000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleImageClick = (comboId) => {
    navigate(`/shop/combo/${comboId}`);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      0: { text: "Ordered", variant: "primary" },
      1: { text: "Dispatched", variant: "warning" },
      2: { text: "Delivered", variant: "success" },
      3: { text: "Cancelled", variant: "danger" }
    };

    const currentStatus = statusMap[status] || { text: "Unknown", variant: "secondary" };
    return <Badge pill bg={currentStatus.variant}>{currentStatus.text}</Badge>;
  };

  if (loading) {
    return null;
  }

  return (
    <Layout title="Orders">
      <Container className="my-5">
        <div className="text-center">
          {!hasOrders ? (
            <img
              src="/shop/images/Shopping.svg"
              alt="No Orders"
              style={{ width: '500px', height: 'auto', marginBottom: '20px' }}
            />
          ) : (
            <img
              src="/shop/images/orderfound.svg"
              alt="Order Found"
              className="d-block d-md-none"
              style={{ width: '300px', height: 'auto', marginBottom: '20px', margin: '0 auto' }}
            />
          )}
        </div>

        {!hasOrders ? (
          <div className="text-center py-5">
            <h3 className="text-muted">No Orders Found</h3>
            <p className="text-muted">You haven't placed any orders yet.</p>
            <button
              className="btn btn-primary mt-3"
              onClick={() => navigate("/shop")}
            >
              Start Shopping
            </button>
          </div>
        ) : (
          <Row className="g-4">
            {orders.map((order) => (
              <Col key={order.id} xs={12}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Header className="bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">
                        Order #<span className="text-primary">{order.id}</span>
                      </h5>
                      <div>
                        {getStatusBadge(order.status)}
                      </div>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Row className="mb-3">
                      <Col xs={12} md={4}>
                        <div className="d-flex align-items-center mb-2">
                          <FaCalendarAlt className="text-muted me-2" />
                          <span>
                            <strong>Order Date:</strong> {new Date(order.created_at).toLocaleDateString("en-GB", {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                      </Col>
                      <Col xs={12} md={4}>
                        <div className="d-flex align-items-center mb-2">
                          <FaRupeeSign className="text-muted me-2" />
                          <span>
                            <strong>Total:</strong> ₹
                            {order.combo_price ? Number(order.combo_price).toFixed(2) : "N/A"}
                          </span>
                        </div>
                      </Col>
                      <Col xs={12} md={4}>
                        <div className="d-flex align-items-center mb-2">
                          <FaCalendarAlt className="text-muted me-2" />
                          <span>
                            <strong>Expected Delivery:</strong>{" "}
                            {order.delivery_date
                              ? new Date(order.delivery_date).toLocaleDateString("en-GB", {
                                day: "numeric",
                                month: "long",
                                year: "numeric"
                              })
                              : "N/A"}
                          </span>
                        </div>
                      </Col>
                    </Row>

                    <hr />

                    <h6 className="mb-3">
                      <strong>Product Details</strong>
                    </h6>
                    <Row>
                      <Col xs={12} sm={6} md={4}>
                        <Card className="h-100 border-0 shadow-sm">
                          <div
                            style={{ cursor: "pointer" }}
                            onClick={() => handleImageClick(order.combo_id)}
                          >
                            <Card.Img
                              variant="top"
                              src={`${process.env.REACT_APP_HOST}storage/${order.combo_image}`}
                              alt={order.combo.name}
                              className="img-fluid rounded-top"
                              style={{ height: "180px", objectFit: "cover" }}
                            />
                          </div>
                          <Card.Body className="text-center">
                            <h6 className="mb-0">
                              <strong>{order.combo.name}</strong>
                            </h6>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Container>
      <br />
      <br />
      <br />
    </Layout>
  );
};

export default Orders;